import { AuthMethod, resolveAuthMethod } from "./authMethod";
import {
    clearSignInRetry,
    consumeAuthorizationRequest,
    hasRetriedSignIn,
    markSignInRetried,
    saveAuthorizationRequest,
} from "./authorizationRequest";
import { readSignOutSignalReason, SIGN_OUT_SIGNAL_KEY, SignOutReason } from "./signOut";
import { showSessionExpired } from "@/api/interceptors";
import { buildAuthorizeUrl, exchangeAuthorizationCode, fetchAuthMethod } from "@/api/oauthApi";
import { useAccountStore } from "@/stores/accountStore";
import { EndSessionReason, isOAuthSession, OAuthSessionStatus, useAuthStore } from "@/stores/authStore";
import { createCodeChallenge, createCodeVerifier, createState } from "@/utils/pkce";

export const CALLBACK_PATH = "/auth/callback";

/** The server compares this against the client's registered redirect URIs as an exact string. */
const getRedirectUri = () => `${window.location.origin}${CALLBACK_PATH}`;

const isCallback = () => window.location.pathname.replace(/\/+$/, "") === CALLBACK_PATH;

/** Keeps the authorization code out of the URL and out of browser history. */
const replaceUrl = (route: string) => {
    window.history.replaceState(null, "", route);
};

const failAuthorization = (route: string) => {
    replaceUrl(route);

    if (hasRetriedSignIn()) {
        clearSignInRetry();
        useAuthStore.getState().setStatus(OAuthSessionStatus.FAILED);
        return;
    }

    markSignInRetried();
    useAuthStore.getState().setStatus(OAuthSessionStatus.SIGNED_OUT);
};

const completeAuthorization = async () => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const request = consumeAuthorizationRequest();
    const failureRoute = request?.route ?? "/";

    if (!code || !request || params.get("state") !== request.state) {
        failAuthorization(failureRoute);
        return;
    }

    try {
        const token = await exchangeAuthorizationCode({
            code,
            codeVerifier: request.codeVerifier,
            redirectUri: getRedirectUri(),
        });

        clearSignInRetry();
        replaceUrl(request.route);
        useAuthStore.getState().setSession(token);
    } catch {
        failAuthorization(failureRoute);
    }
};

const startSession = async () => {
    const { setMethod, setStatus, renew } = useAuthStore.getState();

    if (isCallback()) {
        // Landing here at all means an OAuth call back flow: the redirect URI carries only `code` and `state`.
        setMethod(AuthMethod.OAUTH);
        setStatus(OAuthSessionStatus.EXCHANGING_CODE);
        useAccountStore.getState().clearApiKey();
        await completeAuthorization();
        return;
    }

    let method = resolveAuthMethod(useAccountStore.getState().hasAPIKeyinHash);

    if (method === null) {
        method = await fetchAuthMethod();
        setMethod(method);
        setStatus(OAuthSessionStatus.SIGNED_OUT);
        return;
    }

    setMethod(method);

    if (method !== AuthMethod.OAUTH) {
        setStatus(OAuthSessionStatus.SIGNED_OUT);
        return;
    }

    // A remembered API key from an earlier session would otherwise ride alongside the bearer token.
    useAccountStore.getState().clearApiKey();

    const renewed = await renew();

    // A refused grant ends the OAuth attempt, so the user lands on the API-key form — unless the URL
    // forced OAuth, in which case the redirect can simply be retried.
    if (!renewed && useAuthStore.getState().status === OAuthSessionStatus.SIGNED_OUT) {
        const isUrlOverride = new URLSearchParams(window.location.search).get("authMethod") === AuthMethod.OAUTH;
        if (!isUrlOverride) {
            setMethod(await fetchAuthMethod());
        }
        return;
    }

    if (!renewed) {
        setStatus(OAuthSessionStatus.FAILED);
    }
};

/**
 * Runs before React: the router mounts only once an account resolves, so the callback needs handling
 * outside it. Never rejects — an unexpected throw here would otherwise strand the app on its skeleton
 * with no way out, since nothing is mounted yet to catch it.
 */
export const bootstrapAuth = async () => {
    registerCrossTabSignOutListener();

    try {
        await startSession();
    } catch {
        useAuthStore.getState().setStatus(OAuthSessionStatus.FAILED);
    }
};

const registerCrossTabSignOutListener = () => {
    window.addEventListener("storage", ({ key, newValue }) => {
        if (key === SIGN_OUT_SIGNAL_KEY && isOAuthSession()) {
            // An idle sign-out shows the idle notice here too, not the generic expired modal.
            if (readSignOutSignalReason(newValue) === SignOutReason.IDLE) {
                useAccountStore.getState().forget(true, SignOutReason.IDLE);
                return;
            }

            useAuthStore.getState().endSession(EndSessionReason.LOGOUT);
            showSessionExpired();
        }
    });
};

/** Returns the URL rather than navigating, so the caller stays testable. */
export const createAuthorizationRedirect = async (reason?: SignOutReason): Promise<string> => {
    const codeVerifier = createCodeVerifier();
    const state = createState();
    const { pathname, search, hash } = window.location;

    saveAuthorizationRequest({ codeVerifier, state, route: `${pathname}${search}${hash}` });

    return buildAuthorizeUrl({
        codeChallenge: await createCodeChallenge(codeVerifier),
        state,
        redirectUri: getRedirectUri(),
        reason,
    });
};
